import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Cpu, Database } from "lucide-react";
import { cn } from "@/lib/utils";
import type { WorkflowStepData } from "./workflow-data";

const stepColors: Record<string, { bg: string; border: string; text: string }> = {
  blue: { bg: "bg-blue-50 dark:bg-blue-950/40", border: "border-blue-300 dark:border-blue-700", text: "text-blue-700 dark:text-blue-300" },
  cyan: { bg: "bg-cyan-50 dark:bg-cyan-950/40", border: "border-cyan-300 dark:border-cyan-700", text: "text-cyan-700 dark:text-cyan-300" },
  teal: { bg: "bg-teal-50 dark:bg-teal-950/40", border: "border-teal-300 dark:border-teal-700", text: "text-teal-700 dark:text-teal-300" },
  violet: { bg: "bg-violet-50 dark:bg-violet-950/40", border: "border-violet-300 dark:border-violet-700", text: "text-violet-700 dark:text-violet-300" },
  purple: { bg: "bg-purple-50 dark:bg-purple-950/40", border: "border-purple-300 dark:border-purple-700", text: "text-purple-700 dark:text-purple-300" },
  amber: { bg: "bg-amber-50 dark:bg-amber-950/40", border: "border-amber-300 dark:border-amber-700", text: "text-amber-700 dark:text-amber-300" },
  emerald: { bg: "bg-emerald-50 dark:bg-emerald-950/40", border: "border-emerald-300 dark:border-emerald-700", text: "text-emerald-700 dark:text-emerald-300" },
  indigo: { bg: "bg-indigo-50 dark:bg-indigo-950/40", border: "border-indigo-300 dark:border-indigo-700", text: "text-indigo-700 dark:text-indigo-300" },
  orange: { bg: "bg-orange-50 dark:bg-orange-950/40", border: "border-orange-300 dark:border-orange-700", text: "text-orange-700 dark:text-orange-300" },
};

interface WorkflowPipelineProps {
  steps: WorkflowStepData[];
}

export default function WorkflowPipeline({ steps }: WorkflowPipelineProps) {
  const [selectedStep, setSelectedStep] = useState<string | null>(null);
  const selected = steps.find((s) => s.id === selectedStep);

  return (
    <div className="space-y-4">
      {/* Pipeline boxes */}
      <div className="flex flex-wrap items-center gap-2">
        {steps.map((step, i) => {
          const colors = stepColors[step.color] || stepColors.blue;
          const isActive = selectedStep === step.id;
          return (
            <div key={step.id} className="flex items-center gap-2">
              <Card
                onClick={() =>
                  setSelectedStep(isActive ? null : step.id)
                }
                className={cn(
                  "cursor-pointer select-none transition-all duration-200 border-2 px-3 py-2 min-w-[120px]",
                  colors.bg,
                  colors.border,
                  isActive && "ring-2 ring-primary shadow-md scale-105"
                )}
              >
                <p className={cn("text-sm font-semibold", colors.text)}>
                  {step.label}
                </p>
                {step.subtitle && (
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {step.subtitle}
                  </p>
                )}
              </Card>
              {i < steps.length - 1 && (
                <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
            </div>
          );
        })}
      </div>

      {/* Detail panel */}
      {selected && (
        <Card className="p-4 border-dashed animate-in fade-in-0 slide-in-from-top-2 duration-200">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2 flex-1">
              <h4 className="font-semibold text-sm">{selected.label}</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {selected.description}
              </p>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0 items-end">
              {selected.model && (
                <Badge
                  variant="secondary"
                  className="text-xs gap-1"
                >
                  <Cpu className="h-3 w-3" />
                  {selected.model}
                </Badge>
              )}
              {selected.tables && selected.tables.length > 0 && (
                <div className="flex flex-wrap gap-1 justify-end">
                  {selected.tables.map((t) => (
                    <Badge
                      key={t}
                      variant="outline"
                      className="text-[10px] gap-0.5 font-mono"
                    >
                      <Database className="h-2.5 w-2.5" />
                      {t}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
